import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
// import pdfDataCache from '../documents/pdfDataCache';

// Types
export const AnalysisType = {
  id: '',
  phenomenontime: null,
  resulttime: null,
  procedure: '',
  observedproperty: '',
  numericvalue: null,
  numericvalue_uom: '',
  sample_id: '',
  valueProcessingMethod: '',
  document_id: '',
  limitSymbol: '',
  alphanumericValue: '',
  limitSymbolReferenceCode: '',
  PhysicalProperty_quantity: '',
  PhysicalProperty_parameter: '',
  PhysicalProperty_condition: '',
  auto_increment_id: null
};

// Hook for fetching analyses based on sampleId
export const useAnalyses = (sampleId) => { // Accept sampleId directly
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false); // Start with loading false
  const [error, setError] = useState(null);

  // Memoize the fetch function
  const fetchAnalysesForSample = useCallback(async () => {
    // Only fetch if sampleId is provided
    if (!sampleId) {
      setAnalyses([]); // Clear analyses if no sampleId
      setLoading(false);
      setError(null);
      return; 
    }

    setLoading(true);
    setError(null); 

    try {
      // Query specifically for the given sampleId
      let query = supabase
        .from('document_analysis')
        .select(`
          *,
          PhysicalProperty_quantity_info:document_domainvalueslookup!document_analysis_PhysicalProperty_quantity_fkey(*),
          PhysicalProperty_parameter_info:document_domainvalueslookup!document_analysis_PhysicalProperty_parameter_fkey(*),
          PhysicalProperty_condition_info:document_domainvalueslookup!document_analysis_PhysicalProperty_condition_fkey(*),
          limitSymbol_info:document_domainvalueslookup!document_analysis_limitSymbolReferenceCode_fkey(*),
          numericvalue_uom_info:document_domainvalueslookup!document_analysis_numericvalue_uom_fkey(*),
          valueProcessingMethod_info:document_domainvalueslookup!document_analysis_valueProcessingMethod_fkey(*)
        `)
        .eq('sample_id', sampleId); // Filter directly by sampleId

      const { data, error: queryError } = await query;
      
      if (queryError) throw queryError;
      
      const formattedData = data.map(item => {
        try {
          const processedItem = {
            ...item,
            phenomenontime: item.phenomenontime && isValidDate(item.phenomenontime) ? 
              new Date(item.phenomenontime) : null,
            resulttime: item.resulttime && isValidDate(item.resulttime) ? 
              new Date(item.resulttime) : null,
            parameter_label: item.PhysicalProperty_parameter_info?.description || '',
            uom_label: item.numericvalue_uom_info?.aquo_code || item.numericvalue_uom_info?.description || '',
            raw_relations: {
              PhysicalProperty_quantity_info: item.PhysicalProperty_quantity_info,
              PhysicalProperty_parameter_info: item.PhysicalProperty_parameter_info,
              PhysicalProperty_condition_info: item.PhysicalProperty_condition_info,
              limitSymbol_info: item.limitSymbol_info,
              numericvalue_uom_info: item.numericvalue_uom_info,
              valueProcessingMethod_info: item.valueProcessingMethod_info
            }
          };
          return processedItem;
        } catch (mapError) {
          console.error('Error processing analysis item:', item, mapError);
          return item; // Return original item if processing fails
        }
      });

      setAnalyses(formattedData);
      setError(null); 

    } catch (err) {
      console.error(`Error fetching analyses for sample ${sampleId}:`, err); 
      setError(err.message || 'Fout bij ophalen analyses'); 
      setAnalyses([]); // Clear analyses on error
    } finally {
      setLoading(false); 
    }
  }, [sampleId]); // Dependency is now sampleId

  // Effect to fetch data when sampleId changes
  useEffect(() => {
    fetchAnalysesForSample();
  }, [fetchAnalysesForSample]); // Run effect when the fetch function instance changes (due to sampleId change)

  // Return state and the fetch function if manual refetch is needed elsewhere
  return { analyses, loading, error, refetchAnalyses: fetchAnalysesForSample };
};

// Improved isValidDate function
const isValidDate = (dateValue) => {
  if (!dateValue) return false;
  
  try {
    const date = new Date(dateValue);
    // Check if date is valid and within reasonable range (e.g., not before 1900 or after 2100)
    return !isNaN(date.getTime()) && 
           date.getFullYear() > 1900 && 
           date.getFullYear() < 2100;
  } catch (e) {
    return false;
  }
};

// Helper functions
export const getAnalysisById = async (id) => {
  // Check cache first
  // pdfDataCache logic removed
  
  const { data, error } = await supabase
    .from('document_analysis')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  
  // Cache the result
  // pdfDataCache logic removed
  
  return data;
};

export const createAnalysis = async (analysisData) => {
  const { data, error } = await supabase
    .from('document_analysis')
    .insert([analysisData])
    .select();

  if (error) throw error;
  
  // Invalidate relevant caches
  // pdfDataCache logic removed
  
  return data[0];
};

export const updateAnalysis = async (id, updates) => {
  const { data, error } = await supabase
    .from('document_analysis')
    .update(updates)
    .eq('id', id)
    .select();

  if (error) throw error;
  
  // Invalidate relevant caches
  // pdfDataCache logic removed
  
  return data[0];
};

export const deleteAnalysis = async (id) => {
  // First get the analysis to know which caches to invalidate
  // const analysis = await getAnalysisById(id);
  
  const { error } = await supabase
    .from('document_analysis')
    .delete()
    .eq('id', id);

  if (error) throw error;
  
  // Invalidate relevant caches
  // pdfDataCache logic removed
  
  return true;
};

// Helper function to get analyses by sample ID
export const getAnalysesBySampleId = async (sampleId) => {
  // Check cache first
  // pdfDataCache logic removed
  
  const { data, error } = await supabase
    .from('document_analysis')
    .select(`
      *,
      PhysicalProperty_quantity_info:document_domainvalueslookup!document_analysis_PhysicalProperty_quantity_fkey(*),
      PhysicalProperty_parameter_info:document_domainvalueslookup!document_analysis_PhysicalProperty_parameter_fkey(*),
      PhysicalProperty_condition_info:document_domainvalueslookup!document_analysis_PhysicalProperty_condition_fkey(*),
      limitSymbol_info:document_domainvalueslookup!document_analysis_limitSymbolReferenceCode_fkey(*),
      numericvalue_uom_info:document_domainvalueslookup!document_analysis_numericvalue_uom_fkey(*),
      valueProcessingMethod_info:document_domainvalueslookup!document_analysis_valueProcessingMethod_fkey(*)
    `)
    .eq('sample_id', sampleId);

  if (error) throw error;
  
  // Cache the result
  // pdfDataCache logic removed
  
  return data;
};

// Helper function to invalidate all analysis caches
export const invalidateAnalysisCache = (documentId = null) => {
  // pdfDataCache logic removed
  console.warn("invalidateAnalysisCache called, but pdfDataCache logic is removed.");
};
